import camelcaseKeys from 'camelcase-keys';
import { saveAs } from 'file-saver';
import { stopSubmit } from 'redux-form';
import { eventChannel, END } from 'redux-saga';
import { all, call, cancelled, fork, put, select, take, takeEvery, takeLatest, TakeEffect, // eslint-disable-line @typescript-eslint/no-unused-vars
 } from 'redux-saga/effects';
import { authLogout } from '@/actions/AuthActions';
import { authErrorByLoginTypeRedirect } from '@/actions/AuthErrorActions';
import { FETCH_CANCEL, FETCH_END, FETCH_ERROR, FETCH_START, FETCH_ON_UPLOAD_PROGRESS, FETCH_ON_UPLOAD_SUCCESS, } from '@/actions/FetchActions';
import { API_CREATE_DIRECT_STORAGE_UPLOAD, API_CREATE_FILE_UPLOAD, } from '@/actions/FileActions';
import { API_CREATE_RESOURCE_UPLOAD_TO_STORAGE } from '@/actions/FileActionsTs';
import { PUT_MAINTENANCE_INFO } from '@/actions/MaintenanceActions';
import { updateProffitSearchDevice } from '@/actions/ProffitSearchDeviceAction';
import { STORAGE_USER_DEVICE } from '@/define/const';
import { MAINTENANCE, UNAUTHORIZED_LOGIN_TYPE } from '@/define/response';
import { createValidateErrorData } from '@/helpers/validate';
import { loginPath } from '@/utils/router/links';
import webStorage from '../../helpers/webStorage';
const API_LOADING_SUFFIX = '_LOADING';
const API_SUCCESS_SUFFIX = '_SUCCESS';
const API_FAILURE_SUFFIX = '_FAILURE';
const apiSaga = ({ apiClient }) => {
    /**
     * 異常処理の実行
     * @param form
     * @param invalidWhen
     * @param reject
     * @param error
     * @return {Generator<*>}
     */
    function* handleInvalid({ form, invalidWhen, reject }, error) {
        const { response } = error || {};
        const { data: responseData } = response || {};
        const { code: errorCode } = responseData || {};
        if (reject) {
            // エラー時の挙動を設定済みの場合
            const rejectTarget = errorCode && reject[errorCode] ? reject[errorCode] : reject;
            if (rejectTarget) {
                const targets = Array.isArray(rejectTarget)
                    ? rejectTarget
                    : [rejectTarget];
                for (let i = 0; i < targets.length; i += 1) {
                    const singleTarget = targets[i];
                    if (typeof singleTarget === 'function') {
                        singleTarget(error);
                    }
                    else if (typeof singleTarget === 'object' && singleTarget.type) {
                        yield put(singleTarget);
                    }
                }
            }
        }
        else if (form) {
            // エラー内容を設定するformが設定済みの場合
            const errorMessages = createValidateErrorData(error, invalidWhen);
            yield put(stopSubmit(form, errorMessages));
        }
    }
    /**
     * ログイン種別による認証エラーの処理実行
     */
    function* handleAuthErrorByLoginType() {
        // 既にログアウトされている場合 処理を実行しない
        if (window.location.pathname === loginPath()) {
            return false;
        }
        /** 既にリダイレクトされたか */
        const isRedirect = yield select((state) => state.authError.isRedirectByLoginType);
        if (!isRedirect) {
            // isRedirect の state 更新
            yield put(authErrorByLoginTypeRedirect(true));
            // ログイン種別による認証エラーの場合 ログアウト
            yield put(authLogout());
        }
        return false;
    }
    /**
     * アップロード監視用のチャンネルとAPIクライアントを返す
     * @param payload
     * @param meta
     * @returns {(*|Channel<unknown>)[]}
     */
    function callApiWithOnUploadProgressChannel(payload, meta) {
        // conUploadProgressのコールバックからactionをputするため、eventChannelで待ち受ける
        let emit;
        const channel = eventChannel((emitter) => {
            emit = emitter;
            return () => { };
        });
        // ファイルがアップロードされた場合、アップロード状況を監視
        const onUploadProgress = (progressEvent) => {
            const progress = Math.floor((progressEvent.loaded / progressEvent.total) * 100);
            if (meta.wrapOnUploadProgress) {
                meta.wrapOnUploadProgress(progress);
            }
            emit({ meta, progress });
            if (progress === 100) {
                emit(END);
            }
        };
        const apiPromise = apiClient(meta.fetch, payload, Object.assign(Object.assign({}, meta), { onUploadProgress }));
        return [apiPromise, channel];
    }
    /**
     * アップロード状況監視のchannelを待ち受ける
     * @param channel
     * @returns {Generator<PutEffect<{progress: *, type: string}>|TakeEffect, void, *>}
     */
    function* uploadProgressWatcher(channel) {
        while (true) {
            const { meta, progress } = yield take(channel);
            const type = progress === 100 ? FETCH_ON_UPLOAD_SUCCESS : FETCH_ON_UPLOAD_PROGRESS;
            yield put({
                apiAction: {
                    meta: Object.assign(Object.assign({}, meta), { id: meta.apiId, progress,
                        type }),
                    type,
                },
                type,
            });
        }
    }
    function* handleFetch(action) {
        // type -> reducerのkey
        // payload -> 設定したいリクエスト内容
        // meta -> resource, methodなど通信の設定
        const { meta, payload, type } = action;
        // 通信の開始
        yield all([
            put({ meta, payload, type: `${type}${API_LOADING_SUFFIX}` }),
            put({ apiAction: action, noLoading: meta.noLoading, type: FETCH_START }),
        ]);
        try {
            meta.device = yield select((state) => state.proffitSearchDevice.userDevice);
            let response;
            if ([
                API_CREATE_FILE_UPLOAD,
                API_CREATE_DIRECT_STORAGE_UPLOAD,
                API_CREATE_RESOURCE_UPLOAD_TO_STORAGE,
            ].includes(meta.type)) {
                // アップロード処理の場合、進捗状況の監視を行う
                const [apiPromise, channel] = callApiWithOnUploadProgressChannel(payload, meta);
                yield fork(uploadProgressWatcher, channel);
                response = yield call(() => apiPromise);
            }
            else {
                // 通常のAPI実行
                response = yield call(apiClient, meta.fetch, payload, meta);
            }
            if (response.userDevice) {
                webStorage.set(STORAGE_USER_DEVICE, response.userDevice);
                yield put(updateProffitSearchDevice(response.userDevice));
            }
            const successModel = {
                currentPage: response.currentPage,
                lastPage: response.lastPage,
                meta: Object.assign(Object.assign({}, meta), { fetchStatus: FETCH_END }),
                payload: response.data,
                requestPayload: payload,
                total: response.total,
                type: `${type}${API_SUCCESS_SUFFIX}`,
            };
            // 通信の成功の通知
            yield put(Object.assign({}, successModel));
            // 通信終了
            yield put({
                apiAction: action,
                noLoading: meta.noLoading,
                type: FETCH_END,
            });
            // 実行完了後に呼び出したい処理がある場合
            if (meta && meta.resolve) {
                if (typeof meta.resolve === 'function') {
                    meta.resolve(Object.assign({}, successModel));
                }
                else if (typeof meta.resolve === 'object') {
                    yield put(meta.resolve);
                }
            }
            // responseのファイル保存
            if (meta && meta.isBlob && meta.fileName) {
                saveAs(response.body, meta.fileName);
            }
        }
        catch (error) {
            const { response } = error;
            const { data: responseData } = response || {};
            const { code: errorCode, message: errorMessage } = responseData || {};
            // ログイン種別の認証エラーの場合
            if (errorCode === UNAUTHORIZED_LOGIN_TYPE) {
                yield handleAuthErrorByLoginType();
                return;
            }
            // メンテナンスの場合
            if (errorCode === MAINTENANCE) {
                yield put({
                    payload: camelcaseKeys(responseData || {}),
                    type: PUT_MAINTENANCE_INFO,
                });
            }
            // 通信の失敗の通知
            yield put({
                code: errorCode || null,
                error,
                message: errorMessage || null,
                meta: Object.assign(Object.assign({}, meta), { fetchStatus: FETCH_ERROR }),
                payload: responseData || null,
                requestPayload: payload,
                type: `${type}${API_FAILURE_SUFFIX}`,
            });
            yield fork(handleInvalid, meta || {}, error);
            yield put({
                apiAction: action,
                error,
                noLoading: meta.noLoading,
                type: FETCH_ERROR,
            });
        }
        finally {
            if (yield cancelled()) {
                // キャンセルされている場合
                yield put({
                    apiAction: action,
                    noLoading: meta.noLoading,
                    type: FETCH_CANCEL,
                });
            }
        }
    }
    return function* watchCrudFetch() {
        // metaが存在し、fetchで指定のリソースの操作方法が設定されている場合に、このsagaを実行する
        // cancelPreviousの有無で、前回のリクエストをキャンセルするかどうかを判定
        const reToBeMatched = /^API_/;
        const orList = `(${[
            API_LOADING_SUFFIX,
            API_SUCCESS_SUFFIX,
            API_FAILURE_SUFFIX,
        ].join('|')})$`;
        const reToBeRejected = new RegExp(orList);
        const findApiAction = ({ type }) => type.match(reToBeMatched) && !type.match(reToBeRejected);
        yield all([
            takeLatest((action) => action.meta &&
                action.meta.fetch &&
                action.meta.cancelPrevious &&
                findApiAction(action), handleFetch),
            takeEvery((action) => action.meta &&
                action.meta.fetch &&
                !action.meta.cancelPrevious &&
                findApiAction(action), handleFetch),
        ]);
    };
};
export default apiSaga;
